import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const Adverts = () => {
  const [adverts, setAdverts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdvert, setSelectedAdvert] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [advertToDelete, setAdvertToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    type: '',
    company: '',
    image: '',
    video: ''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php', 'adverts_V2'); // Fetch adverts data
        if (response.success) {
          setAdverts(Array.isArray(response.data) ? response.data : []);
        } else {
          toast.error(response.message || "Failed to load adverts data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch adverts");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      type: '',
      company: '',
      image: '',
      video: ''
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (advertItem) => {
    setSelectedAdvert(advertItem);
    setFormData({
      type: advertItem.type || '',
      company: advertItem.company || '',
      image: advertItem.image || '',
      video: advertItem.video || ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (advertItem) => {
    setAdvertToDelete(advertItem);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'adverts_V2', advertToDelete.id); // Delete advert by ID
      if (response.success) {
        toast.success(`Advert deleted successfully`);
        setAdverts(adverts.filter(item => item.id !== advertToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete advert");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete advert");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setAdvertToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    const maxSizeMB = 5; // 5MB limit

    if (file && file.size > maxSizeMB * 1124 * 1124) {
      toast.error(`File size must not exceed ${maxSizeMB}MB. If you ignore this and proceed to add, your selected image will not be vissible to the public.`);
      return;
    }

    if (field === 'image' && file) {
      // Check file type
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
        toast.error("Please upload a valid image (JPEG, PNG, or GIF)");
        return;
      }

      // Check image dimensions
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const { width, height } = img;
        const bannerType = formData.type;

        if (bannerType === 'Billboard' && (width > 1200 || height > 300)) {
          toast.error("Billboard image dimensions must not exceed 1200x300 pixels");
          URL.revokeObjectURL(img.src);
          return;
        }
        if (bannerType === 'Portrait' && (width > 300 || height > 1050)) {
          toast.error("Portrait image dimensions must not exceed 300x1050 pixels");
          URL.revokeObjectURL(img.src);
          return;
        }

        // If dimensions are valid, update formData
        setFormData(prev => ({
          ...prev,
          [field]: file
        }));
        URL.revokeObjectURL(img.src); // Clean up
      };
      img.onerror = () => {
        toast.error("Failed to load image for dimension validation");
        URL.revokeObjectURL(img.src);
      };
    } else if (field === 'video' && file) {
      // Check video file type
      if (!['video/mp4', 'video/webm'].includes(file.type)) {
        toast.error("Please upload a valid video (MP4 or WebM)");
        return;
      }
      setFormData(prev => ({
        ...prev,
        [field]: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;

      if (selectedAdvert) {
        // Update existing advert
        response = await updateData('proccess.php', 'adverts_V2', formData, selectedAdvert.id); // Update advert
        if (response.success) {
          toast.success(`Advert updated successfully`);
          setAdverts(adverts.map(item => 
            item.id === selectedAdvert.id 
              ? { ...item, ...formData, image: formData.image && typeof formData.image === 'string' ? formData.image : item.image, video: formData.video && typeof formData.video === 'string' ? formData.video : item.video } 
              : item
          ));
        }
      } else {
        // Add new advert
        response = await addData('proccess.php', 'adverts_V2', formData); // Add advert
        if (response.success) {
          toast.success(`Advert added successfully`);
          // Assuming response.data is either the new advert or the updated list
          if (Array.isArray(response.data)) {
            setAdverts(response.data);
          } else {
            setAdverts(prev => [...prev, response.data]);
          }
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedAdvert(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "type", headerName: "Banner Type", flex: 1, minWidth: 120 },
    { field: "company", headerName: "Company", flex: 1, minWidth: 120 },
    {
      field: "image",
      headerName: "Advert Image",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Advert"}
              style={{ width: 50, height: 50, objectFit: "cover" }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    {
      field: "video",
      headerName: "Advert Video",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          {params.value ? (
            <a
              href={`https://mindsinaction.com.na/assets/media/videos/${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.blue[400], textDecoration: 'underline' }}
            >
              View Video
            </a>
          ) : (
            "No video"
          )}
        </Box>
      )
    },
    { field: "dateCreated", headerName: "Date Created", flex: 1, minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No adverts available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedAdvert(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedAdvert ? "Edit Advert" : "Add New Advert"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                select
                style={{ width: "100%" }}
                id="type"
                name="type"
                label="Banner Type"
                variant="standard"
                value={formData.type}
                onChange={handleFormChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select Banner Type</option>
                <option value="Billboard">Leaderboard</option>
                <option value="Portraite">Portraite</option>
              </TextField>
              <TextField
                style={{ width: "100%" }}
                id="company"
                name="company"
                label="Company"
                variant="standard"
                value={formData.company}
                onChange={handleFormChange}
                required
              />
              <Box width={"100%"}>
                <label>Advert Image</label>
                <Input
                  type="file"
                  style={{ width: "100%" }}
                  id="image"
                  name="image"
                  onChange={(e) => handleFileChange(e, 'image')}
                  required={!selectedAdvert}
                  disabled={!formData.type}
                />
                {formData.type && (
                  <Typography variant="caption" color={colors.dark[400]}>
                    {formData.type === 'Billboard'
                      ? "Max dimensions: 1200x300 pixels, Max size: 5MB"
                      : "Max dimensions: 300x1050 pixels, Max size: 5MB"}
                  </Typography>
                )}
                {!formData.type && (
                  <Typography color="error" variant="caption">
                    Please select a banner type first
                  </Typography>
                )}
              </Box>
              <Box width={"100%"}>
                <label>Advert Video</label>
                <Input
                  type="file"
                  style={{ width: "100%" }}
                  id="video"
                  name="video"
                  onChange={(e) => handleFileChange(e, 'video')}
                />
                <Typography variant="caption" color={colors.dark[400]}>
                  Max size: 5MB
                </Typography>
              </Box>
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedAdvert(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedAdvert ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the advert for "{advertToDelete?.company}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Adverts Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage adverts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            '&:hover': {
              backgroundColor: colors.blue[500],
            }
          }}
        >
          Add Advert
        </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "calc(100vh - 250px)", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={adverts}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Adverts;